import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { useApi } from '~/contexts';
import { colors } from '~/styles';
import {
  Grid,
  Card,
  CardLabel,
  CardValue,
  CardSubValue,
  SectionTitle,
  ChartLabel,
  ChartWrapper,
  formatNumber,
} from './shared';

export default function Summary({ start, end, granularity = 'day' }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!start || !end) return;
    let cancelled = false;
    const activeUsersMethod = {
      day: 'getDailyActiveUsers',
      week: 'getWeeklyActiveUsers',
      month: 'getMonthlyActiveUsers',
    };
    const fetchData = async () => {
      setIsLoading(true);
      setData(null);
      setActivity(null);
      try {
        const [summaryRes, activityRes, activeUsersRes] = await Promise.all([
          api.admin.aiDashboard.getSummary({ start, end }),
          api.admin.aiDashboard.getActivityOverTime({ start, end, granularity }),
          api.admin.reports[activeUsersMethod[granularity] || 'getDailyActiveUsers']({ start, end }),
        ]);
        if (!cancelled) {
          setData(summaryRes.data);
          // Merge logged-in user counts into activity data by date
          const loginMap = {};
          for (const row of activeUsersRes.data) {
            loginMap[row.date] = row.count;
          }
          setActivity(
            activityRes.data.map((d) => ({
              ...d,
              loggedInMembers: loginMap[moment.utc(d.date).format('YYYY-MM-DD')] || 0,
            })),
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [api, start, end, granularity]);

  const formatMap = { day: 'MMM D', week: 'MMM D', month: 'MMM YYYY' };
  const dateFormat = formatMap[granularity] || 'MMM D';
  const labelMap = { day: 'Daily', week: 'Weekly', month: 'Monthly' };
  const granularityLabel = labelMap[granularity] || 'Daily';

  const membersChart = useMemo(() => {
    if (!activity) return null;
    return {
      labels: activity.map((d) => moment.utc(d.date).format(dateFormat)),
      datasets: [
        {
          label: 'Logged In',
          type: 'line',
          yAxisID: 'y',
          order: 0,
          data: activity.map((d) => d.loggedInMembers),
          borderColor: colors.grey25,
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          pointHitRadius: 6,
          tension: 0.3,
        },
        {
          label: 'AI Active',
          yAxisID: 'y1',
          order: 1,
          data: activity.map((d) => d.activeMembers),
          backgroundColor: colors.primary,
        },
      ],
    };
  }, [activity, dateFormat]);

  if (isLoading || !data) {
    return (
      <>
        <SectionTitle>Active Workspaces &amp; Members</SectionTitle>
        <Grid>
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardLabel>&nbsp;</CardLabel>
              <CardValue>&mdash;</CardValue>
            </Card>
          ))}
        </Grid>
      </>
    );
  }

  return (
    <>
      <SectionTitle>Active Workspaces &amp; Members</SectionTitle>
      <Grid>
        <Card>
          <CardLabel>Active Workspaces</CardLabel>
          <CardValue>{formatNumber(data.activeWorkspaces)}</CardValue>
          <CardSubValue>
            Out of <strong>{formatNumber(data.totalWorkspaces)}</strong> AI-enabled workspaces
          </CardSubValue>
        </Card>
        <Card>
          <CardLabel>Active Members</CardLabel>
          <CardValue>{formatNumber(data.activeMembers)}</CardValue>
          <CardSubValue>
            Out of <strong>{formatNumber(data.totalMembers)}</strong> members in AI-enabled workspaces
          </CardSubValue>
        </Card>
        <Card>
          <CardLabel>Adoption Rate</CardLabel>
          <CardValue>
            {data.totalMembers > 0 ? ((data.activeMembers / data.totalMembers) * 100).toFixed(1) : 0}%
          </CardValue>
          <CardSubValue>Of members in AI-enabled workspaces</CardSubValue>
        </Card>
      </Grid>
      <ChartLabel>Active Members ({granularityLabel})</ChartLabel>
      <ChartWrapper>
        <Bar
          data={membersChart}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index' },
            plugins: { legend: { display: true } },
            scales: {
              y: {
                position: 'left',
                title: { display: true, text: 'Logged In' },
                ticks: { precision: 0 },
                beginAtZero: true,
              },
              y1: {
                position: 'right',
                title: { display: true, text: 'AI Active' },
                ticks: { precision: 0 },
                beginAtZero: true,
                grid: { drawOnChartArea: false },
              },
            },
          }}
        />
      </ChartWrapper>
    </>
  );
}
